'use client';
import { yupResolver } from '@hookform/resolvers/yup';
import Link from 'next/link';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';

import Button from '../Button';
import CountrySelect from '../CountrySelect';
import GlobalLoader from '../GlobalLoader';
import PhoneIcon from '../Icons/Phone';
import Input from '../Input';
import Modal from '../Modal';
import SuccessModal from '../SuccessModal';

import { postRequestCallback } from '@/actions/contacts';
import { IContactPageDictionary } from '@/dictionaries/pages/contacts/types';
import { ICountriesDictionary } from '@/dictionaries/utils/countries/types';
import useCountries from '@/hooks/useCountries';
import { cn } from '@/lib/utils';
import { removeBodyScrollbar } from '@/utils/helpers';
import { Title, Description } from '@radix-ui/react-dialog';
import { Locale } from '../../../i18n.config';
import { getBaseTranslatedSchema } from '../ContactForm/schema';

type Props = {
  dict: IContactPageDictionary;
  countriesDict: ICountriesDictionary;
  //countries: Country[];
  lang: Locale;
  children?: React.ReactNode;
};

declare const window: Window & { dataLayer: Record<string, unknown>[] };

export default function RequestCallbackModal({
  dict,
  countriesDict,
  //countries,
  lang,
  children,
}: Props) {
  const [successModalIsOpen, setSuccessModalIsOpen] = useState(false);
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [blockSubmit, setBlockSubmit] = useState(false);

  const schema = getBaseTranslatedSchema(dict.form.errors);

  const { form, successModal } = dict;

  const { translatedCountries, languageCountry } = useCountries({
    lang,
    countriesDict,
  });

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setValue,
    watch,
    reset,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      country_info: {
        name: languageCountry?.name,
        code: languageCountry?.code,
        id: languageCountry?.id,
        phone: {
          code: languageCountry?.phoneCode,
          country: languageCountry?.name,
        },
      },
    },
  });

  function handleSetCountry(code: string, name: string, id: number) {
    setValue('country_info.name', name);
    setValue('country_info.code', code);
    setValue('country_info.id', id);

    const foundCountry = translatedCountries.find(
      (country) => country.name === name,
    );

    if (foundCountry) {
      setValue('country_info.phone.code', foundCountry.phoneCode);
      setValue('country_info.phone.country', foundCountry.name);
    }
  }

  const onSubmit = handleSubmit(async (data) => {
    if (blockSubmit) return;
    setBlockSubmit(true);

    const requestData = {
      firstName: data.name,
      email: data.email,
      lastName: data.surname,
      address: {
        country: data.country_info.id,
      },
      phoneCode: data.country_info.id,
      phone: data.phone.toString(),
      brochureCountry: data.country_info.id,
      privacyPolicy: true,
      type: 'callback_request',
      language: lang,
    };

    try {
      const response = await postRequestCallback(requestData);
      if (response.data) {
        reset();
        setModalIsOpen(false);
        setSuccessModalIsOpen(true);
        setBlockSubmit(false);

        window.dataLayer = window.dataLayer || [];

        window.dataLayer.push({
          event: 'eventCallBack',
          eventCategory: 'contact_callback',
          eventAction: 'form_callback',
          eventLabel: window.location.href,
          language: lang,
        });
      } else {
        setBlockSubmit(false);
      }
    } catch (error) {
      console.error(error);
    }
  });

  useEffect(() => {
    if (modalIsOpen || successModalIsOpen) {
      removeBodyScrollbar(true);
    } else {
      removeBodyScrollbar(false);
    }
  }, [modalIsOpen, successModalIsOpen]);

  return (
    <>
      {successModalIsOpen && (
        <SuccessModal
          modalIsOpen={successModalIsOpen}
          setModalIsOpen={setSuccessModalIsOpen}
          title={successModal.requestCallback.title}
          description={successModal.requestCallback.description}
        />
      )}
      <Modal.Root open={modalIsOpen} onOpenChange={setModalIsOpen}>
        <Modal.Trigger>
          {children ?? (
            <Button
              variant="outline"
              className="flex [&>svg]:hover:text-white ">
              <PhoneIcon className="fill-current text-graphite w-8 min-w-8 h-9 min-h-9 mr-4 transition ease-in duration-300 group-hover:animate-btn-color" />
              <span>{form.requestCallback}</span>
            </Button>
          )}
        </Modal.Trigger>
        <Modal.Content
          contentCustomClass="tablet:mb-[130px]"
          onOpenChange={setModalIsOpen}>
          <Title className="hidden" />
          <Description className="hidden" />
          <div className="flex flex-col p-4 relative">
            <GlobalLoader isLoading={isSubmitting} />
            <div className="mb-8 text-center">
              <h3 className="mb-2 text-medium font-bold">{form.modalTitle}</h3>
              <p>{form.modalSubtitle}</p>
            </div>
            <form onSubmit={onSubmit} className="flex flex-col gap-4">
              <div className="flex flex-col gap-4 tablet:gap-0 desktop:flex-row desktop:justify-between">
                <Input.Container className="desktop:w-[calc(50%-1rem)]">
                  <Input.Label htmlFor="name">{form.name}</Input.Label>
                  <Input.Root register={register} name="name" />
                  <Input.Error error={errors.name} />
                </Input.Container>

                <Input.Container className="desktop:w-[calc(50%-1rem)]">
                  <Input.Label htmlFor="surname">{form.surname}</Input.Label>
                  <Input.Root register={register} name="surname" />
                  <Input.Error error={errors.surname} />
                </Input.Container>
              </div>
              <div className="flex flex-col gap-4 desktop:gap-0 desktop:flex-row desktop:justify-between">
                <div className="desktop:w-[calc(50%-1rem)] h-fit">
                  <CountrySelect
                    countries={translatedCountries}
                    label={form.country}
                    id={'country-selector'}
                    onChange={({ code, name, id }) =>
                      handleSetCountry(code, name, id)
                    }
                    selectedValue={translatedCountries.find(
                      (option) => option.name === watch('country_info.name'),
                    )}
                  />
                </div>

                <div className="flex flex-col desktop:w-[calc(50%-1rem)]">
                  <label
                    className="text-sm text-gray-700 leading-[1.8] w-fit"
                    htmlFor="phone-country-selector">
                    {form.phone}
                  </label>
                  <div className="flex flex-row">
                    <div className="w-[250px] tablet:w-[280px] desktop:w-[200px] h-fit">
                      <CountrySelect
                        countries={translatedCountries}
                        label=""
                        id={'phone-country-selector'}
                        showPhoneCode
                        onChange={({ id, name }) => {
                          setValue('country_info.phone.code', id);
                          setValue('country_info.phone.country', name);
                        }}
                        selectedValue={translatedCountries.find(
                          (option) =>
                            option.name === watch('country_info.phone.country'),
                        )}
                      />
                    </div>
                    <Input.Container className="w-full">
                      <Input.Root register={register} name="phone" />
                      <Input.Error error={errors.phone} />
                    </Input.Container>
                  </div>
                </div>
              </div>

              <Input.Container>
                <Input.Label htmlFor="email">{form.email}</Input.Label>
                <Input.Root register={register} name="email" />
                <Input.Error error={errors.email} />
              </Input.Container>

              <div className="flex flex-col gap-4 desktop:flex-row desktop:justify-between desktop:items-center desktop:mt-4">
                <div className="flex flex-col desktop:w-[calc(50%-1rem)]">
                  <div className="w-full flex flex-col gap-4">
                    <div className="flex items-center gap-2 tablet:w-80 lg:w-[30rem] desktop:w-full">
                      <input
                        {...register('terms')}
                        className={cn('checkbox-input', {
                          'border border-underline': watch('terms') == true,
                        })}
                        onChange={(e) => setValue('terms', e.target.checked)}
                        type="checkbox"
                        name="checkbox-input"
                      />
                      <label
                        className="text-sm text-footer-gray"
                        htmlFor="checkbox-input">
                        {form.terms}
                        <Link
                          href={form.privacyHref}
                          target="_blank"
                          className="hover:text-gray-700 underline cursor-pointer ml-1 text-footer-gray">
                          {form.privacyPolicy}
                        </Link>
                      </label>
                    </div>
                  </div>
                  <Input.Error error={errors.terms} />
                </div>

                <Button
                  type="submit"
                  className={cn('desktop:w-[calc(50%-1rem)]', {
                    'hover:bg-primary hover:text-white': blockSubmit,
                  })}
                  disabled={blockSubmit}>
                  {form.sendRequest}
                </Button>
              </div>
            </form>
          </div>
        </Modal.Content>
      </Modal.Root>
    </>
  );
}
